/**
 * NotificationSettingsDialog - Dialog for configuring card notification preferences
 */
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Bell, 
  Mail,
  Loader2,
  Check
} from 'lucide-react';

const NotificationSettingsDialog = ({
  open,
  onOpenChange,
  notificationPrefs,
  setNotificationPrefs,
  onSave,
  saving,
  onSendTestAlert,
  hasSelectedCard
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-full" data-testid="notification-settings-btn">
          <Mail className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" /> Notification Settings
          </DialogTitle>
          <DialogDescription>Configure email alerts for spending notifications</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Alerts</Label>
              <p className="text-xs text-muted-foreground">Receive spending alerts via email</p>
            </div>
            <Switch 
              checked={notificationPrefs.email_alerts_enabled} 
              onCheckedChange={(v) => setNotificationPrefs(p => ({ ...p, email_alerts_enabled: v }))} 
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Budget Alerts</Label>
              <p className="text-xs text-muted-foreground">Alert when category budgets are exceeded</p>
            </div>
            <Switch 
              checked={notificationPrefs.budget_alerts_enabled} 
              onCheckedChange={(v) => setNotificationPrefs(p => ({ ...p, budget_alerts_enabled: v }))} 
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Weekly Summary</Label>
              <p className="text-xs text-muted-foreground">Receive weekly spending summary</p>
            </div>
            <Switch 
              checked={notificationPrefs.weekly_summary_enabled} 
              onCheckedChange={(v) => setNotificationPrefs(p => ({ ...p, weekly_summary_enabled: v }))} 
            />
          </div>
          <div className="space-y-2">
            <Label>Warning Threshold: {notificationPrefs.alert_threshold_warning}%</Label>
            <Slider 
              value={[notificationPrefs.alert_threshold_warning]} 
              onValueChange={([v]) => setNotificationPrefs(p => ({ ...p, alert_threshold_warning: v }))} 
              min={50} 
              max={90} 
              step={5} 
            />
          </div>
          <div className="space-y-2">
            <Label>Critical Threshold: {notificationPrefs.alert_threshold_critical}%</Label>
            <Slider 
              value={[notificationPrefs.alert_threshold_critical]} 
              onValueChange={([v]) => setNotificationPrefs(p => ({ ...p, alert_threshold_critical: v }))} 
              min={80} 
              max={100} 
              step={5} 
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={onSave} disabled={saving} className="flex-1 rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Preferences
            </Button>
            {hasSelectedCard && (
              <Button variant="outline" onClick={onSendTestAlert} className="rounded-xl">
                <Mail className="w-4 h-4 mr-2" /> Test
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
